import React, { useEffect, useState, useRef, useCallback } from 'react';
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import './FbImage.css';

// Skeleton Loader Component
const SkeletonLoader = ({ className = '' }) => (
    <div className={`skeleton-loader ${className}`}>
        <div className="skeleton-shimmer"></div>
    </div>
);

// Error State Component
const ErrorState = ({ alt, onRetry }) => (
    <div className="image-error-state">
        <div className="error-icon">⚠️</div>
        <div className="error-message">Failed to load image</div>
        {onRetry && (
            <button className="error-retry-button" onClick={onRetry}>
                Retry
            </button>
        )}
        {alt && <span className="sr-only">{alt}</span>}
    </div>
);

const FbImage = ({ path, thumbnailPath, alt, className = '', ...rest }) => {
    const [url, setUrl] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [lowQualityUrl, setLowQualityUrl] = useState('');
    const imgRef = useRef(null);
    const retryCountRef = useRef(0);
    const maxRetries = 3;

    const loadImage = useCallback(async () => {
        if (!path) {
            setError(true);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(false);
            const storage = getStorage();

            if (thumbnailPath) {
                getDownloadURL(ref(storage, thumbnailPath))
                    .then((thumbURL) => setLowQualityUrl(thumbURL))
                    .catch((thumbErr) => {
                        console.warn('Could not load thumbnail placeholder:', thumbErr);
                    });
            }

            const coverURL = await getDownloadURL(ref(storage, path));
            setUrl(coverURL);
        } catch (err) {
            console.error('Error loading image:', err);
            if (retryCountRef.current < maxRetries) {
                retryCountRef.current += 1;
                setTimeout(() => loadImage(), 1000 * retryCountRef.current);
            } else {
                setError(true);
                setLoading(false);
            }
        }
    }, [path, thumbnailPath]);

    useEffect(() => {
        retryCountRef.current = 0;
        setUrl('');
        setLowQualityUrl('');
        setImageLoaded(false);
        setError(false);
        loadImage();
    }, [loadImage]);

    const handleImageLoad = () => {
        setImageLoaded(true);
        setLoading(false);
    };

    const handleImageError = () => {
        setError(true);
        setLoading(false);
    };

    const handleRetry = useCallback(() => {
        retryCountRef.current = 0;
        setError(false);
        loadImage();
    }, [loadImage]);

    if (error) {
        return <ErrorState alt={alt} onRetry={handleRetry} />;
    }

    return (
        <div className={`fb-image-wrapper ${className} ${imageLoaded ? 'image-loaded' : ''}`}>
            {loading && <SkeletonLoader className="fb-image-skeleton" />}
            {lowQualityUrl && !imageLoaded && (
                <img
                    src={lowQualityUrl}
                    alt=""
                    className="fb-image-placeholder"
                    aria-hidden="true"
                />
            )}
            {url && (
                <img
                    ref={imgRef}
                    src={url}
                    alt={alt || 'Image'}
                    className={`fb-image ${imageLoaded ? 'fb-image-loaded' : 'fb-image-loading'} ${lowQualityUrl ? 'fb-image-blur-up' : ''}`}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    loading="lazy"
                    {...rest}
                />
            )}
        </div>
    );
};

export default FbImage;
